import React, { memo } from "react";
import styled, { css, useTheme } from "styled-components";
import { useSelector } from "react-redux";
import {
  CoinName,
  LanguageNameSpaces,
  PrecisionType,
  TopMarketsTableDataBySymbol,
} from "@shared/types";
import { ICON_TYPES } from "@shared/common/constants";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { getLocaleStringWithPrecision } from "@shared/utils/numbers";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { DISPLAY_CURRENCIES } from "common/constants";
import IconWithFallback from "Global/IconWithFallback";
import Skeleton from "common/NewLook/Skeleton.tsx";
import {
  directionColor,
  getNewLookTokens,
  newLookType,
} from "common/NewLook/tokens.ts";

const NARROW_LIST_WIDTH = 780;

interface MarketsListProps {
  rows: TopMarketsTableDataBySymbol[];
  loading: boolean;
  onOpen: (symbol: CoinName) => void;
}

/**
 * Markets as a compact list: the price, its 24h move in colour, and where the
 * price sits in its 24h range (a small range bar with the low and high), then
 * the volume.
 */
const MarketsList = ({ rows, loading, onOpen }: MarketsListProps) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.DASHBOARD);
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  return (
    <Table role="table">
      <Grid as="div" role="row" $header={true}>
        <span role="columnheader">{t("market.header.pair")}</span>
        <End role="columnheader">{t("market.header.last_price")}</End>
        <End role="columnheader">{t("market.header.24h_change")}</End>
        <span role="columnheader" className="wide">
          {t("market.header.24h_low_high")}
        </span>
        <End role="columnheader" className="wide">
          {t("market.header.24h_volume")}
        </End>
      </Grid>
      {loading && !rows.length
        ? Array.from({ length: 5 }, (_, index) => (
            <Grid as="div" key={index}>
              <Pair>
                <Skeleton width={24} height={24} radius={12} />
                <Skeleton width={80} height={12} />
              </Pair>
              <End>
                <Skeleton width={64} height={12} />
              </End>
              <End>
                <Skeleton width={48} height={12} />
              </End>
              <span className="wide">
                <Skeleton width="100%" height={6} radius={3} />
              </span>
              <End className="wide">
                <Skeleton width={72} height={12} />
              </End>
            </Grid>
          ))
        : rows.map((row) => (
            <MarketRow key={row.symbol} row={row} onOpen={onOpen} />
          ))}
      {!loading && !rows.length && <Empty>{tCommon("empty.no_data")}</Empty>}
    </Table>
  );
};

export default memo(MarketsList);

const MarketRow = memo(
  ({
    row,
    onOpen,
  }: {
    row: TopMarketsTableDataBySymbol;
    onOpen: (symbol: CoinName) => void;
  }) => {
    const theme = useTheme();
    const tokens = getNewLookTokens(theme);
    const displayCurrency = useSelector(selectDisplayCurrency);
    const isUsd = displayCurrency === DISPLAY_CURRENCIES.USD;
    const { getMinimumPrecision, getPrecision } = usePrecisions();
    const [base, quote] = row.symbol.split("/");
    const change = (row.change || 0) * 100;
    const pricePrecision = getMinimumPrecision(
      PrecisionType.PRICE,
      row.symbol,
      row.exchanges
    );
    // The day's low and high read like the last price: same precision.
    const rangePrecision = isUsd
      ? pricePrecision
      : getPrecision(PrecisionType.BTC_PRICE);
    const volumePrecision = getPrecision(
      isUsd ? PrecisionType.USD_AMOUNT : PrecisionType.BTC_AMOUNT
    );
    const currency = isUsd ? "$" : "₿";
    const low = isUsd ? row.low_price[0] : row.low_price[1];
    const high = isUsd ? row.high_price[0] : row.high_price[1];
    const volume = isUsd ? row.volume[0] : row.volume[1];

    // Position in the day's range, from the day's own series so the price
    // and the range share units whatever the quote currency.
    const series = (row.graph_data || [])
      .map((point) => Number(point[1]))
      .filter((value) => Number.isFinite(value));
    let position: number | undefined;
    if (series.length > 1) {
      const min = Math.min(...series);
      const max = Math.max(...series);
      if (max > min) position = (series[series.length - 1] - min) / (max - min);
    }
    const compactVolume =
      volume !== null && volume !== undefined
        ? `${currency}${new Intl.NumberFormat("en-US", {
            notation: "compact",
            maximumFractionDigits: 2,
          }).format(volume)}`
        : "—";

    return (
      <Grid
        as="div"
        role="row"
        tabIndex={0}
        $clickable={true}
        onClick={() => onOpen(row.symbol)}
        onKeyDown={(event) => event.key === "Enter" && onOpen(row.symbol)}
      >
        <Pair role="cell">
          <IconWithFallback
            coinOrExchangeName={row.symbol}
            iconType={ICON_TYPES.COIN}
            pxIconSize={24}
          />
          <PairName>
            {base}
            {quote && <Quote>/{quote}</Quote>}
          </PairName>
        </Pair>
        <End role="cell">
          <Figure>
            {getLocaleStringWithPrecision(row.price, pricePrecision)}
          </Figure>
        </End>
        <End role="cell">
          <Figure style={{ color: directionColor(tokens, change) }}>
            {change > 0 ? "+" : change < 0 ? "−" : ""}
            {Math.abs(change).toFixed(2)}%
          </Figure>
        </End>
        <Range role="cell" className="wide">
          <RangeTrack>
            {position !== undefined && (
              <>
                <RangeFill
                  style={{
                    width: `${position * 100}%`,
                    background: directionColor(tokens, change),
                  }}
                />
                <RangeMarker style={{ left: `${position * 100}%` }} />
              </>
            )}
          </RangeTrack>
          <RangeLabels>
            <span>
              {low !== null && low !== undefined
                ? `${currency}${getLocaleStringWithPrecision(
                    low,
                    rangePrecision
                  )}`
                : "—"}
            </span>
            <span>
              {high !== null && high !== undefined
                ? `${currency}${getLocaleStringWithPrecision(
                    high,
                    rangePrecision
                  )}`
                : "—"}
            </span>
          </RangeLabels>
        </Range>
        <End role="cell" className="wide">
          <Figure
            title={getLocaleStringWithPrecision(volume || 0, volumePrecision)}
          >
            {compactVolume}
          </Figure>
        </End>
      </Grid>
    );
  }
);

const Table = styled.div`
  container-type: inline-size;
  @container (max-width: ${NARROW_LIST_WIDTH}px) {
    .wide {
      display: none !important;
    }
  }
`;
const Grid = styled.div<{ $header?: boolean; $clickable?: boolean }>`
  display: grid;
  grid-template-columns:
    minmax(140px, 1.6fr) minmax(96px, 0.9fr) minmax(84px, 0.7fr)
    minmax(180px, 300px) minmax(96px, 0.8fr);
  column-gap: 24px;
  align-items: center;
  padding: 0 20px;
  min-height: ${(p) => (p.$header ? 34 : 56)}px;
  outline: none;
  transition: background-color 120ms;
  @container (max-width: ${NARROW_LIST_WIDTH}px) {
    grid-template-columns: minmax(120px, 1.2fr) minmax(90px, 1fr) minmax(
        76px,
        0.8fr
      );
  }
  ${(p) =>
    p.$header
      ? css`
          ${newLookType.micro};
          color: ${getNewLookTokens(p.theme).muted};
          border-bottom: 1px solid ${getNewLookTokens(p.theme).hairline};
        `
      : css`
          & + & {
            border-top: 1px solid ${getNewLookTokens(p.theme).hairline};
          }
        `}
  ${(p) =>
    p.$clickable &&
    css`
      cursor: pointer;
      &:hover {
        background: ${getNewLookTokens(p.theme).rowHover};
      }
      &:focus-visible {
        box-shadow: inset 0 0 0 2px ${getNewLookTokens(p.theme).focus};
      }
      &:last-child {
        border-radius: 0 0 12px 12px;
      }
    `}
`;
const Pair = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;
const PairName = styled.span`
  font-size: 14px;
  line-height: 18px;
  font-weight: 520;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Quote = styled.span`
  font-weight: 500;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const End = styled.div`
  display: flex;
  justify-content: flex-end;
  min-width: 0;
  text-align: right;
`;
const Figure = styled.span`
  ${newLookType.figure};
  font-size: 14px;
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
`;
const Range = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;
const RangeTrack = styled.div`
  position: relative;
  height: 4px;
  border-radius: 2px;
  background: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
`;
const RangeFill = styled.span`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  border-radius: 2px;
  opacity: 0.35;
`;
const RangeMarker = styled.span`
  position: absolute;
  top: 50%;
  width: 8px;
  height: 8px;
  margin: -4px 0 0 -4px;
  border-radius: 50%;
  background: ${(p) => getNewLookTokens(p.theme).ink};
  box-shadow: 0 0 0 2px ${(p) => getNewLookTokens(p.theme).panel};
`;
const RangeLabels = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 8px;
  ${newLookType.meta};
  font-size: 11px;
  line-height: 14px;
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Empty = styled.div`
  padding: 24px 20px;
  text-align: center;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
